import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, PackageCheck, Search, Package } from "lucide-react";

/**
 * PackagesPage — full CRUD for the front-desk package log.
 * Log packages when carriers drop them off, edit locker/tracking, mark
 * picked up (and undo), or delete stale ones.
 */
const CARRIERS = ["UPS", "USPS", "FedEx", "Amazon", "DHL", "OnTrac", "Other"];

export default function PackagesPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    carrier: "UPS", tracking: "", unit: "", resident_name: "", locker: "",
  });

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data } = await api.get(`/packages?property_id=${propertyId}`);
      setItems(data.items || []);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }, [propertyId]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ carrier: "UPS", tracking: "", unit: "", resident_name: "", locker: "" });
    setDialogOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      carrier: p.carrier || "UPS", tracking: p.tracking || "", unit: p.unit || "",
      resident_name: p.resident_name || "", locker: p.locker || "",
    });
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.tracking.trim() || !form.unit.trim() || !form.resident_name.trim()) {
      return toast.error("Tracking, unit, and resident name are required");
    }
    const body = {
      carrier: form.carrier, tracking: form.tracking.trim(),
      unit: form.unit.trim(), resident_name: form.resident_name.trim(),
      locker: form.locker.trim() || null,
    };
    try {
      if (editing) {
        await api.patch(`/packages/${editing.id}`, body);
        toast.success("Package updated");
      } else {
        await api.post(`/packages?property_id=${propertyId}`, body);
        toast.success("Package logged");
      }
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const pickup = async (id) => {
    try { await api.patch(`/packages/${id}/pickup`); toast.success("Marked picked up"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const undoPickup = async (id) => {
    try { await api.patch(`/packages/${id}`, { status: "awaiting_pickup" }); toast.success("Undone"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete package for ${p.resident_name} (${p.tracking})?`)) return;
    try { await api.delete(`/packages/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const filtered = useMemo(() => items.filter((p) => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return [p.tracking, p.unit, p.resident_name, p.carrier, p.locker]
      .some((v) => (v || "").toLowerCase().includes(s));
  }), [items, q, statusFilter]);

  const kpis = useMemo(() => ({
    awaiting: items.filter((p) => p.status === "awaiting_pickup").length,
    picked: items.filter((p) => p.status === "picked_up").length,
  }), [items]);

  return (
    <div>
      <PageHeader
        eyebrow="Concierge"
        title="Package Management"
        subtitle="Every carrier, every locker. Log delivery → mark picked up → clear the shelf."
        actions={
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-amber-500/40 text-amber-300 bg-amber-500/10" data-testid="pkg-awaiting-badge">
              {kpis.awaiting} awaiting
            </Badge>
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10">
              {kpis.picked} picked up
            </Badge>
            <Button size="sm" onClick={openNew} className="bg-orange-500 hover:bg-orange-600 gap-2" data-testid="pkg-new-btn">
              <Plus className="h-4 w-4" /> Log package
            </Button>
          </div>
        }
      />

      <Card className="border-white/10 bg-card p-4 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input data-testid="pkg-search" className="pl-9 bg-white/[0.02] border-white/10" placeholder="Search tracking / unit / resident / carrier" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="pkg-status-filter" className="w-44 bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="awaiting_pickup">Awaiting pickup</SelectItem>
              <SelectItem value="picked_up">Picked up</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-white/10 bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead>Carrier</TableHead>
              <TableHead>Tracking</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Resident</TableHead>
              <TableHead>Locker</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((p) => (
              <TableRow key={p.id} className="border-white/5" data-testid={`pkg-row-${p.id}`}>
                <TableCell>{p.carrier}</TableCell>
                <TableCell className="font-mono text-xs">{p.tracking}</TableCell>
                <TableCell>{p.unit}</TableCell>
                <TableCell>{p.resident_name}</TableCell>
                <TableCell>{p.locker || "—"}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={p.status === "picked_up"
                    ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                    : "border-orange-500/40 text-orange-300 bg-orange-500/10"}>
                    {(p.status || "").replace("_", " ")}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    {p.status === "awaiting_pickup" && (
                      <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-300 gap-1" onClick={() => pickup(p.id)} data-testid={`pkg-pickup-${p.id}`}>
                        <PackageCheck className="h-3 w-3" /> Picked up
                      </Button>
                    )}
                    {p.status === "picked_up" && (
                      <Button size="sm" variant="ghost" onClick={() => undoPickup(p.id)} data-testid={`pkg-undo-${p.id}`} title="Undo pickup">
                        Undo
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`pkg-edit-${p.id}`}>
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-rose-300" onClick={() => remove(p)} data-testid={`pkg-delete-${p.id}`}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground text-sm" data-testid="pkg-empty">
                <Package className="h-6 w-6 mx-auto mb-2 opacity-60" />
                No packages match — log one when a carrier drops off.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-white/10" data-testid="pkg-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit package" : "Log new package"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Carrier</Label>
                <Select value={form.carrier} onValueChange={(v) => setForm({ ...form, carrier: v })}>
                  <SelectTrigger data-testid="pkg-form-carrier"><SelectValue /></SelectTrigger>
                  <SelectContent>{CARRIERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tracking #</Label>
                <Input data-testid="pkg-form-tracking" value={form.tracking} onChange={(e) => setForm({ ...form, tracking: e.target.value })} placeholder="1Z999AA10123456784" />
              </div>
              <div>
                <Label>Unit</Label>
                <Input data-testid="pkg-form-unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="12B" />
              </div>
              <div>
                <Label>Resident name</Label>
                <Input data-testid="pkg-form-resident" value={form.resident_name} onChange={(e) => setForm({ ...form, resident_name: e.target.value })} placeholder="Jane Smith" />
              </div>
              <div className="col-span-2">
                <Label>Locker # (optional)</Label>
                <Input data-testid="pkg-form-locker" value={form.locker} onChange={(e) => setForm({ ...form, locker: e.target.value })} placeholder="L-14 or Package Room shelf 3" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={save} data-testid="pkg-form-save">
              {editing ? "Save" : "Log package"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
